import { useCallback, useMemo } from 'react';
import { useToast } from '@kcd/design-system';

import { SectionDivider } from '~/components/SectionDividor';
import { CartGroupHeader } from '~/domains/cart/components/CartGroupHeader';
import { useToggleVendorGroupAllItemsSelect } from '~/domains/cart/queries';
import { useLinkComponent } from '~/hooks/mobileApp';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { OAISchema } from '~/types/openapi';
import { changedByFontScale } from '~/utils/fontScale';

import { VendorBundles } from '../VendorBundles';

import styles from './index.module.css';

const CartVendor = ({
  data,
}: {
  data: OAISchema['MarketShoppingCartVendorGroupResponse'];
}) => {
  const LinkComponent = useLinkComponent();
  const toast = useToast();

  const toggleAllMutate = useToggleVendorGroupAllItemsSelect();

  const isAllChecked = useMemo(() => {
    const flatList = data.shoppingCartItemBundles
      .map(({ shoppingCartItems }) => shoppingCartItems)
      .flat();

    const checked =
      !!flatList.length && flatList.every(({ isSelected }) => !!isSelected);

    return checked;
  }, [data]);

  const toggleVendorGroup = useCallback(() => {
    toggleAllMutate(
      {
        requestBody: {
          vendorId: data.vendorId,
          isSelected: !isAllChecked,
          cartType: 'SHOPPING_CART',
        },
      },
      {
        onError: (err) => {
          if (err instanceof OAIApiError) {
            const errMessage = getOAIErrorMessage(err);
            toast(errMessage, { offset: 100 });
          }
        },
      },
    );
  }, [toggleAllMutate, data.vendorId, isAllChecked, toast]);

  if (!data.shoppingCartItemBundles.length) return <></>;

  return (
    <>
      <div className={styles.wrap}>
        <CartGroupHeader
          checked={isAllChecked}
          title={data.mallName || data.vendorName || ''}
          onChange={toggleVendorGroup}
          deliveryType={data.deliveryType}
        />
        <VendorBundles
          vendorId={data.vendorId}
          shoppingCartItemBundles={data.shoppingCartItemBundles}
        />

        <LinkComponent href={`/vendors/${data.vendorId}`}>
          <div className={styles.vendorLink}>
            {`${data?.mallName || data?.vendorName || ''}`}
            {changedByFontScale() && <br />} 상품 추가하기
          </div>
        </LinkComponent>
      </div>
      <SectionDivider />
    </>
  );
};

type Props = {
  data: OAISchema['MarketShoppingCartVendorGroupResponse'][];
};

export const CartVendorGroup = ({ data }: Props) => {
  return (
    <>
      {data.map((item) => {
        return <CartVendor key={item.deliveryPolicyId} data={item} />;
      })}
    </>
  );
};
